import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SortingState } from "@tanstack/react-table";
import {
    Search,
    LayoutGrid,
    Table as TableIcon,
} from "@/components/icons";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
    DropdownMenuSeparator,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useUnitsData } from "@/hooks/useUnitsData";
import { useStats } from "@/hooks/useStats";
import { useAuth } from "@/app/AuthContext";
import { useColumnConfig } from "@/hooks/useColumnConfig";
import { ChargerTable } from "@/components/ChargerTable";
import { ChargerVitrine } from "@/components/ChargerVitrine";
import { ChargerDetailPanel } from "@/views/ChargerDetailPanel";
import { SlidePanel } from "@/components/SlidePanel";
import { ColumnChooser } from "@/components/ColumnChooser";
import type { Unit } from "@/lib/api";
import { useCatalog } from "@/app/localization";

export interface UnitsExplorerFilters {
    search: string;
    stateFilter: string[];
    sortBy: string;
    viewMode: "table" | "vitrine";
    // Charger type: "residential" | "commercial" (work_mode). Empty = all types.
    typeFilter: string[];
    // Pricing: "paid" | "free" (pricing_mode), only meaningful for commercial
    // chargers. Empty = any pricing.
    pricingFilter: string[];
    // Protocol versions ("0", "9", …) — admin-only control. Empty = all.
    protocolFilter: string[];
}

export interface UnitsExplorerProps {
    filters: UnitsExplorerFilters;
    onFiltersChange: (filters: UnitsExplorerFilters) => void;
    // When the caller is a residential driver, the "no units" empty state offers
    // a self-claim CTA instead of the "contact your administrator" dead-end.
    canClaim?: boolean;
    onClaim?: () => void;
}

export function sanitizeTypePricing(
    typeFilter: string[],
    pricingFilter: string[],
): { typeFilter: string[]; pricingFilter: string[] } {
    if (!typeFilter.includes("commercial")) {
        return { typeFilter, pricingFilter: [] };
    }
    return { typeFilter, pricingFilter };
}

const PAGE_SIZE = 100;

// Values are the API/filter codes; labels come from the catalog at render.
const COLUMN_IDS = [
    "status",
    "name",
    "unit_id_col",
    "product",
    "voltage",
    "current",
    "session_energy",
    "power_factor",
    "protocol_version",
    "location",
] as const;

const COLUMN_LABEL_KEY: Record<(typeof COLUMN_IDS)[number], string> = {
    status: "operator.units.column_status",
    name: "operator.units.column_unit",
    unit_id_col: "operator.units.column_unit_id",
    product: "operator.units.column_product",
    voltage: "operator.metric.voltage",
    current: "operator.metric.current",
    session_energy: "operator.metric.session_energy",
    power_factor: "operator.metric.power_factor",
    protocol_version: "operator.units.column_protocol",
    location: "operator.units.column_location",
};

const STATUS_VALUES = ["charging", "standby", "ready", "connected", "blocked", "error", "offline", "unsupported"] as const;
const TYPE_VALUES = ["residential", "commercial"] as const;
const PRICING_VALUES = ["paid", "free"] as const;
const SORT_VALUES = ["name", "unit_id", "state", "current", "power"] as const;

function sortingToParam(sorting: SortingState, fallback: string): string {
    if (!sorting.length) return fallback;
    const s = sorting[0];
    const map: Record<string, string> = {
        name: "name",
        status: "state",
        voltage: "voltage",
        current: "current",
        session_energy: "power",
        unit_id_col: "unit_id",
    };
    return map[s.id] || fallback;
}

export function UnitsExplorer({ filters, onFiltersChange, canClaim = false, onClaim }: UnitsExplorerProps) {
    const { search, stateFilter, sortBy, viewMode, typeFilter, pricingFilter, protocolFilter } = filters;
    const stateParam = stateFilter.join(",");
    const modeParam = typeFilter.join(",");
    const pricingParam = pricingFilter.join(",");
    const protocolParam = protocolFilter.join(",");
    // Protocol filter is an admin diagnostic; options come from the fleet
    // stats snapshot (protocol_counts — {} for org-scoped users).
    const { isAdmin } = useAuth();
    const { t, catalog } = useCatalog();
    const { protocolCounts } = useStats();
    const protocolOptions = Object.entries(protocolCounts)
        .sort((a, b) => Number(b[1]) - Number(a[1]));

    const update = useCallback(
        (patch: Partial<UnitsExplorerFilters>) =>
            onFiltersChange({ ...filters, ...patch }),
        [filters, onFiltersChange],
    );

    const commercialSelected = typeFilter.includes("commercial");
    // Pricing is a commercial-only concept; show it once Commercial is picked,
    // or while a pricing value is still active (so the user can clear it).
    const showPricing = commercialSelected || pricingFilter.length > 0;

    const toggleType = useCallback(
        (value: string, active: boolean) => {
            const nextTypes = active
                ? typeFilter.filter((t) => t !== value)
                : [...typeFilter, value];
            // Whenever Commercial ends up unselected, drop any pricing filter so
            // a now-hidden pricing value can't keep silently narrowing results.
            update(sanitizeTypePricing(nextTypes, pricingFilter));
        },
        [typeFilter, pricingFilter, update],
    );

    const togglePricing = useCallback(
        (value: string, active: boolean) => {
            update({
                pricingFilter: active
                    ? pricingFilter.filter((p) => p !== value)
                    : [...pricingFilter, value],
            });
        },
        [pricingFilter, update],
    );

    // TanStack sorting state (internal)
    const [sorting, setSorting] = useState<SortingState>([]);

    // Sync TanStack sorting → parent sortBy
    useEffect(() => {
        const param = sortingToParam(sorting, sortBy);
        if (param !== sortBy) update({ sortBy: param });
    }, [sorting]);

    // Paging
    const [pageEnd, setPageEnd] = useState(PAGE_SIZE);
    useEffect(() => setPageEnd(PAGE_SIZE), [search, stateParam, sortBy, modeParam, pricingParam, protocolParam]);

    // Data — join arrays into comma-separated strings for the API
    const { units, total, noAccess, degraded, truncated, pageLimit, isLoading } = useUnitsData({
        search: search.trim(),
        state: stateParam,
        sort: sortBy,
        start: 0,
        end: pageEnd,
        mode: modeParam,
        pricing: pricingParam,
        protocol: protocolParam,
    });

    // A truncated page is the server's page limit: asking for a wider window
    // returns the same rows, so stop offering more and ask to narrow instead.
    const hasMore = units.length < total && !truncated;
    const loadMore = useCallback(() => setPageEnd((p) => p + PAGE_SIZE), []);

    // Column config
    const { columnVisibility, setColumnVisibility, resetToDefaults } = useColumnConfig();

    // Detail panel
    const [selectedUnitId, setSelectedUnitId] = useState<string | null>(
        () => localStorage.getItem("dashboardSelectedUnit")
    );
    const handleUnitClick = useCallback((unit: Unit) => {
        setSelectedUnitId(unit.unit_id);
        localStorage.setItem("dashboardSelectedUnit", unit.unit_id);
    }, []);
    // Double-click opens the full charger details page (single click = panel).
    const navigate = useNavigate();
    const handleUnitOpen = useCallback(
        (unit: Unit) => navigate(`/unit/${unit.unit_id}`),
        [navigate],
    );
    const closePanel = useCallback(() => {
        setSelectedUnitId(null);
        localStorage.removeItem("dashboardSelectedUnit");
    }, []);

    const handleColumnToggle = useCallback(
        (colId: string, visible: boolean) => {
            setColumnVisibility((prev) => ({ ...prev, [colId]: visible }));
        },
        [setColumnVisibility],
    );

    const columnDefs = React.useMemo(
        () => COLUMN_IDS.map((id) => ({ id, label: t(COLUMN_LABEL_KEY[id]) })),
        [t, catalog],
    );
    const statusLabel = (value: string) => t("operator.units.status_option", { status: value, raw: value });
    const typeLabel = (value: string) => t("operator.units.type_option", { type: value, raw: value });
    const pricingLabel = (value: string) => t("operator.units.pricing_option", { pricing: value, raw: value });

    const statusSet = new Set(stateFilter);
    const statusCount = stateFilter.length;
    const typeSet = new Set(typeFilter);
    const typeCount = typeFilter.length;
    const pricingSet = new Set(pricingFilter);
    const pricingCount = pricingFilter.length;

    return (
        <>
            {/* Controls */}
            <Card>
                <CardContent className="gap-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                        {/* Search */}
                        <div className="flex flex-1 min-w-[240px]">
                            <Button variant="ghost" size="sm" aria-label={t("operator.units.search_label")}>
                                <Search className="h-4 w-4"/>
                            </Button>
                            <input
                                className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                aria-label={t("operator.units.search_label")}
                                placeholder={t("operator.units.search_placeholder")}
                                value={search}
                                onChange={(e) => update({ search: e.target.value })}
                            />
                        </div>

                        {/* State filter (multi-select) */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-1.5">
                                    {statusCount === 0
                                        ? t("operator.units.filter_all_statuses")
                                        : statusCount === 1
                                            ? statusLabel(stateFilter[0])
                                            : t("operator.units.filter_statuses")}
                                    {statusCount > 1 && (
                                        <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                                            {statusCount}
                                        </Badge>
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-52">
                                {STATUS_VALUES.map((value) => {
                                    const active = statusSet.has(value);
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={value}
                                            checked={active}
                                            onSelect={(e) => {
                                                e.preventDefault();
                                                const next = active
                                                    ? stateFilter.filter((s) => s !== value)
                                                    : [...stateFilter, value];
                                                update({ stateFilter: next });
                                            }}
                                        >
                                            {statusLabel(value)}
                                        </DropdownMenuCheckboxItem>
                                    );
                                })}
                                {statusCount > 0 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onSelect={() => update({ stateFilter: [] })}
                                            className="text-xs opacity-70"
                                        >
                                            {t("operator.units.filter_clear")}
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Type filter (residential / commercial, multi-select) */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="gap-1.5">
                                    {typeCount === 0
                                        ? t("operator.units.filter_all_types")
                                        : typeCount === 1
                                            ? typeLabel(typeFilter[0])
                                            : t("operator.units.filter_types")}
                                    {typeCount > 1 && (
                                        <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                                            {typeCount}
                                        </Badge>
                                    )}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-48">
                                {TYPE_VALUES.map((value) => {
                                    const active = typeSet.has(value);
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={value}
                                            checked={active}
                                            onSelect={(e) => {
                                                e.preventDefault();
                                                toggleType(value, active);
                                            }}
                                        >
                                            {typeLabel(value)}
                                        </DropdownMenuCheckboxItem>
                                    );
                                })}
                                {typeCount > 0 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onSelect={() => update({ typeFilter: [], pricingFilter: [] })}
                                            className="text-xs opacity-70"
                                        >
                                            {t("operator.units.filter_clear")}
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Pricing filter (paid / free) — commercial chargers only */}
                        {showPricing && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="gap-1.5">
                                        {pricingCount === 0
                                            ? t("operator.units.filter_all_pricing")
                                            : pricingCount === 1
                                                ? pricingLabel(pricingFilter[0])
                                                : t("operator.units.filter_pricing")}
                                        {pricingCount > 1 && (
                                            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                                                {pricingCount}
                                            </Badge>
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-40">
                                    {PRICING_VALUES.map((value) => {
                                        const active = pricingSet.has(value);
                                        return (
                                            <DropdownMenuCheckboxItem
                                                key={value}
                                                checked={active}
                                                onSelect={(e) => {
                                                    e.preventDefault();
                                                    togglePricing(value, active);
                                                }}
                                            >
                                                {pricingLabel(value)}
                                            </DropdownMenuCheckboxItem>
                                        );
                                    })}
                                    {pricingCount > 0 && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                onSelect={() => update({ pricingFilter: [] })}
                                                className="text-xs opacity-70"
                                            >
                                                {t("operator.units.filter_clear")}
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Protocol version filter — admin diagnostic. Options
                            come from the fleet stats snapshot with live counts;
                            filtering happens server-side (?protocol=). */}
                        {isAdmin && protocolOptions.length > 0 && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="gap-1.5">
                                        {protocolFilter.length === 0
                                            ? t("operator.units.filter_all_protocols")
                                            : protocolFilter.length === 1
                                                ? t("operator.units.filter_protocol_one", { version: protocolFilter[0] })
                                                : t("operator.units.filter_protocols")}
                                        {protocolFilter.length > 1 && (
                                            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] leading-4">
                                                {protocolFilter.length}
                                            </Badge>
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-44">
                                    {protocolOptions.map(([ver, count]) => {
                                        const active = protocolFilter.includes(ver);
                                        return (
                                            <DropdownMenuCheckboxItem
                                                key={ver}
                                                checked={active}
                                                onSelect={(e) => {
                                                    e.preventDefault();
                                                    update({
                                                        protocolFilter: active
                                                            ? protocolFilter.filter((p) => p !== ver)
                                                            : [...protocolFilter, ver],
                                                    });
                                                }}
                                            >
                                                {t("operator.units.protocol_option", { version: ver, count: Number(count) })}
                                            </DropdownMenuCheckboxItem>
                                        );
                                    })}
                                    {protocolFilter.length > 0 && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                onSelect={() => update({ protocolFilter: [] })}
                                                className="text-xs opacity-70"
                                            >
                                                {t("operator.units.filter_clear")}
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Sort */}
                        <Select value={sortBy} onValueChange={(v) => {
                            update({ sortBy: v });
                            setSorting([]);
                        }}>
                            <SelectTrigger size="sm">
                                <SelectValue placeholder={t("operator.units.sort", { by: "name" })}/>
                            </SelectTrigger>
                            <SelectContent>
                                {SORT_VALUES.map((value) => (
                                    <SelectItem key={value} value={value}>{t("operator.units.sort", { by: value })}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* View toggle */}
                        <ToggleGroup type="single" size="sm" variant="outline" value={viewMode}
                                     onValueChange={(v) => v && update({ viewMode: v as "table" | "vitrine" })}>
                            <ToggleGroupItem value="table" aria-label={t("operator.view.table")}>
                                <TableIcon className="h-4 w-4"/>
                            </ToggleGroupItem>
                            <ToggleGroupItem value="vitrine" aria-label={t("operator.units.view_cards")}>
                                <LayoutGrid className="h-4 w-4"/>
                            </ToggleGroupItem>
                        </ToggleGroup>

                        {/* Column chooser (table only) */}
                        {viewMode === "table" && (
                            <ColumnChooser
                                columns={columnDefs}
                                visibility={columnVisibility}
                                onToggle={handleColumnToggle}
                                onReset={resetToDefaults}
                            />
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Units Content + Detail Panel */}
            <div className="flex gap-4 items-stretch">
                {/* Main content — shrinks when panel is open */}
                <div className="flex-1 min-w-0">
                    {degraded && (
                        <div role="alert" className="mb-3 rounded-md bg-amber-400/15 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                            {t("operator.units.list_degraded")}
                        </div>
                    )}
                    {truncated && !degraded && (
                        <div role="status" className="mb-3 rounded-md bg-muted px-3 py-2 text-sm opacity-80">
                            {t("operator.units.list_truncated", { shown: pageLimit ?? units.length, total })}
                        </div>
                    )}
                    {noAccess ? (
                        <Card>
                            <CardContent className="items-center text-center">
                                {canClaim ? (
                                    <>
                                        <div className="text-4xl">🔌</div>
                                        <div className="text-lg font-semibold">{t("operator.units.no_chargers_title")}</div>
                                        <div className="opacity-70">
                                            {t("operator.units.no_chargers_body")}
                                        </div>
                                        <Button className="mt-3" onClick={onClaim}>
                                            {t("operator.claim.open")}
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-4xl">🔒</div>
                                        <div className="text-lg font-semibold">{t("operator.units.none_assigned_title")}</div>
                                        <div className="opacity-70">
                                            {t("operator.units.none_assigned_body")}
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    ) : viewMode === "table" ? (
                        <ChargerTable
                            units={units}
                            total={total}
                            hasMore={hasMore}
                            isLoading={isLoading}
                            onLoadMore={loadMore}
                            onRowClick={handleUnitClick}
                            onRowDoubleClick={handleUnitOpen}
                            selectedUnitId={selectedUnitId ?? undefined}
                            columnVisibility={columnVisibility}
                            onColumnVisibilityChange={setColumnVisibility}
                            sorting={sorting}
                            onSortingChange={setSorting}
                        />
                    ) : (
                        <ChargerVitrine
                            units={units}
                            total={total}
                            isLoading={isLoading}
                            onUnitClick={handleUnitClick}
                            onUnitDoubleClick={handleUnitOpen}
                            selectedUnitId={selectedUnitId ?? undefined}
                        />
                    )}
                </div>

                {/* Detail panel — inline, pushes content left */}
                <SlidePanel
                    open={!!selectedUnitId}
                    onClose={closePanel}
                    title={t("operator.units.panel_title")}
                >
                    {selectedUnitId && <ChargerDetailPanel unitId={selectedUnitId}/>}
                </SlidePanel>
            </div>
        </>
    );
}
